/**
 * PLT-6826 — what a row of the supplier results panel opens to.
 *
 * Its own file rather than more of `supplier-results-panel.tsx`, which is already the size the
 * conventions warn about: this is the part of the row that is new, and it is read on its own.
 */
import { Button } from '@upright/ui/common/generic/components/button';
import { Skeleton, SkeletonProvider } from '@upright/ui/common/generic/components/skeleton';
import { Text } from '@upright/ui/common/generic/components/typography';
// Not from the `icons` barrel, which does not re-export it — the same direct import the panel's own
// footer uses.
import AiSparklesIcon from '@upright/ui/icons/ai-sparkles-streamline-core';
import styles from './supplier-results-panel.module.css';

/**
 * Where a row's line is: here, on its way, or not coming.
 *
 * `unavailable` is a line neither request could supply — the take-aways request failing or refused —
 * not a row without one: the server answers every row it bands, falling back to the template where
 * the model writes nothing. See `takeawayStateFor`.
 */
export type TakeawayState = { kind: 'ready'; line: string } | { kind: 'writing' } | { kind: 'unavailable' };

/**
 * The disclosure chevron, drawn here rather than imported.
 *
 * **The curated icon set has no chevron**, and the set is being pruned under PLT-6810 (#8753) as this
 * lands, so a new glyph there from this branch would collide with that prune. Every attribute is
 * `icons/arrow-right.tsx`'s — the 24 box, `fill="none"`, `currentColor`, round caps and joins, the 2.5
 * stroke — so it is the same family rather than a chevron from somewhere else, and the path is that
 * arrow's head alone, re-centred and cut to a 7.5 × 15 V that reads as a disclosure at 16px.
 *
 * TODO(PLT-6826): move this into `src/ui/icons/` once PLT-6810 has merged, and import it from there.
 */
export function DisclosureChevron() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={16} height={16} aria-hidden={true}>
      <path
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2.5}
        d="M8.25 4.5 15.75 12 8.25 19.5"
      />
    </svg>
  );
}

/**
 * The block under an open row: the take-away, the conditions a hazard was read under, and one way on.
 *
 * **No heading inside it.** The row directly above is the heading, and a "Key take-aways" label over
 * one sentence in a 360px panel is a second claim on a line that already has one.
 *
 * **No AI marker** (Roni, 2026-09-23). The impact summary trails its statement with a sparkle and an
 * "AI-generated summary" tooltip; this panel deliberately does not, so the line reads as Upright's own
 * account of the risk. That is a decision about this surface, not a gap to close by copying the marker.
 *
 * **The line is `gray-600` regular, the qualifier `sm`.** `design/10-foundations.md` makes a title and
 * the line under it one pairing, regular weight in `gray-600` whatever the title is, and the qualifier
 * is incidental meta — so the block reads in three steps: the row, what it means, what it was read under.
 *
 * **The way on is text with the AI mark, left under its block** — inline in content rather than in a
 * panel footer (`design/50-components.md`), and text rather than filled because it is a way out to
 * another surface. It is absent, not disabled, where the viewer has no agent to hand off to.
 */
export function TakeawayBlock({
  idPrefix,
  rowKey,
  state,
  qualifier,
  onAskInChat,
}: {
  idPrefix: string;
  rowKey: string;
  state: TakeawayState;
  /** The conditions this row was read under. Banded hazard rows only. */
  qualifier: string | undefined;
  onAskInChat: (() => void) | undefined;
}) {
  return (
    <div
      className={styles.takeaway}
      id={takeawayRegionId(idPrefix, rowKey)}
      role="region"
      aria-labelledby={riskRowId(idPrefix, rowKey)}
      aria-busy={state.kind === 'writing'}
    >
      <TakeawayLine state={state} />
      {qualifier !== undefined && (
        <Text size="sm" height="xs" color="gray-600">
          {qualifier}
        </Text>
      )}
      {onAskInChat !== undefined && (
        // The wrapper carries the optical pull-left; `Button` takes no `className` of its own.
        <span className={styles.takeawayAction}>
          <Button variant="text" size="small" startIcon={AiSparklesIcon} onPress={onAskInChat}>
            Ask about this in chat
          </Button>
        </span>
      )}
    </div>
  );
}

function TakeawayLine({ state }: { state: TakeawayState }) {
  if (state.kind === 'writing') {
    return <WritingSkeleton />;
  }
  if (state.kind === 'unavailable') {
    // The panel's own wording for a failed fetch: what happened, and what to do next.
    return (
      <Text size="md" height="sm" color="gray-600">
        This summary couldn’t be loaded. Refresh, or try again in a minute.
      </Text>
    );
  }
  return (
    <Text size="md" height="sm" color="gray-600">
      {state.line}
    </Text>
  );
}

/**
 * The shape of a topic line still on its way, on a row opened before it lands: for a second or two on
 * a supplier's first visit, while the lines are being written. Once they are cached the lines request
 * reads no climate section and usually answers before the bands (measured on waffle, 2026-09-23), so
 * this seldom shows then — but the two requests race, so it can. Hazard rows never show it: their line
 * comes with the bands. Two bars rather than one, because a line runs to two or three at this width
 * and a single bar would collapse upward the moment the text arrives. The strings are stand-ins:
 * `Skeleton` paints over its child and makes its text transparent.
 */
function WritingSkeleton() {
  return (
    <SkeletonProvider isLoading={true}>
      <Skeleton>
        <Text size="md" height="sm">
          The line for this row is being written for
        </Text>
      </Skeleton>
      <Skeleton>
        <Text size="md" height="sm">
          its first reader.
        </Text>
      </Skeleton>
    </SkeletonProvider>
  );
}

/**
 * The row control's id, which the region it opens names as its label.
 *
 * Two things make a row key unfit to be an id as it stands, and both fail silently. Keys carry the
 * topic's display name, spaces included, and `aria-controls` / `aria-labelledby` are space-separated id
 * lists — so "topic-Own workforce" would point at two ids that do not exist. And one page can hold more
 * than one list (Storybook's docs page renders every story at once), so the list's own `useId` prefix is
 * what keeps two lists' rows from answering to the same id.
 */
export function riskRowId(idPrefix: string, rowKey: string): string {
  return `${idPrefix}-row-${encodeURIComponent(rowKey)}`;
}

/** The open region's id, which the row control names in `aria-controls`. See {@link riskRowId}. */
export function takeawayRegionId(idPrefix: string, rowKey: string): string {
  return `${idPrefix}-takeaway-${encodeURIComponent(rowKey)}`;
}
